import React, { useState, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { GlgSelect, FaIcon } from 'SharedComponents'
import { publishCascade, publishCategoryChange, subscribeToCascade, subscribeToCategoryChange } from './cascade'

const ALL = 'all'
const NO_SEASON = 'none'

const t = (key, opts) => (window.I18n ? window.I18n.t(`feed_event_picker.${key}`, opts) : '')

const matchesSeason = (event, seasonFilter) => {
  if (seasonFilter === ALL) return true
  if (seasonFilter === NO_SEASON) return !event.season_id
  return event.season_id === seasonFilter
}

const categoriesShareOneRule = () => {
  const state = (window.glg && window.glg.feed_settings && window.glg.feed_settings.picker_state) || {}
  const rules = Object.keys(state).map((key) => {
    const entry = state[key]

    return JSON.stringify([
      entry.mode,
      entry.mode === 'by_category' ? [ ...entry.listIds ].sort() : [],
      entry.mode === 'manual' ? [ ...entry.selectedIds ].sort() : [],
    ])
  })

  return rules.length > 0 && new Set(rules).size === 1
}

const FeedEventPicker = ({ events, categoryKey, initialMode, initialListIds, customerLists, seasons, cascade, initialCustom }) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState(initialMode || 'all')
  const [listIds, setListIds] = useState(initialListIds || [])
  const [searchQuery, setSearchQuery] = useState('')
  const [seasonFilter, setSeasonFilter] = useState(() => (seasons.find(s => s.current) || {}).value || ALL)
  const [categoryFilter, setCategoryFilter] = useState(ALL)
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(events.filter(e => e.selected).map(e => String(e.id)))
  )

  const hasPublishedRef = useRef(false)
  const [isCustom, setIsCustom] = useState(Boolean(initialCustom))

  useEffect(() => {
    if (cascade) return undefined

    window.glg = window.glg || {}
    window.glg.feed_settings = window.glg.feed_settings || {}
    window.glg.feed_settings.picker_state = window.glg.feed_settings.picker_state || {}
    window.glg.feed_settings.picker_state[categoryKey] = {
      mode,
      selectedIds: [...selectedIds],
      listIds,
    }

    return () => {
      if (window.glg?.feed_settings?.picker_state) {
        delete window.glg.feed_settings.picker_state[categoryKey]
      }
    }
  }, [selectedIds, categoryKey, mode, listIds, cascade])

  useEffect(() => {
    if (cascade) return

    publishCategoryChange()
  }, [cascade, mode, listIds, selectedIds])

  useEffect(() => {
    if (!cascade) return

    if (!hasPublishedRef.current) {
      hasPublishedRef.current = true
      return
    }

    publishCascade({ mode, listIds, selectedIds: [...selectedIds] })
    setIsCustom(false)
  }, [cascade, mode, listIds, selectedIds])

  useEffect(() => {
    if (cascade) return undefined

    return subscribeToCascade((selection) => {
      setMode(selection.mode)
      setListIds(selection.listIds)
      setSelectedIds(new Set(selection.selectedIds))
    })
  }, [cascade])

  useEffect(() => {
    if (!cascade) return undefined

    return subscribeToCategoryChange(() => setIsCustom(!categoriesShareOneRule()))
  }, [cascade])

  const seasonOptions = useMemo(() => [
    ...seasons.filter(s => s.current),
    { value: ALL, label: t('all_seasons') },
    { value: NO_SEASON, label: t('no_season') },
    ...seasons.filter(s => !s.current),
  ], [seasons])

  const categoryOptions = useMemo(() => [
    { value: ALL, label: t('all_categories') },
    ...customerLists.map(l => ({ value: l.id, label: l.name })),
  ], [customerLists])

  const filteredEvents = useMemo(() => events.filter(e => {
    if (searchQuery && !e.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
    if (!matchesSeason(e, seasonFilter)) return false
    if (categoryFilter !== ALL && !(e.list_ids || []).includes(categoryFilter)) return false
    return true
  }), [events, searchQuery, seasonFilter, categoryFilter])

  const allFilteredSelected = filteredEvents.length > 0 &&
    filteredEvents.every(e => selectedIds.has(String(e.id)))

  const toggleEvent = (id) => {
    const strId = String(id)
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(strId)) { next.delete(strId) } else { next.add(strId) }
      return next
    })
  }

  const selectAll = () =>
    setSelectedIds(prev => new Set([...prev, ...filteredEvents.map(e => String(e.id))]))

  const deselectAll = () => {
    const filteredIds = new Set(filteredEvents.map(e => String(e.id)))
    setSelectedIds(prev => new Set([...prev].filter(id => !filteredIds.has(id))))
  }

  const toggleList = (listId) => {
    setListIds(prev =>
      prev.includes(listId) ? prev.filter(id => id !== listId) : [...prev, listId]
    )
  }

  const allListsSelected = customerLists.length > 0 &&
    customerLists.every(l => listIds.includes(l.id))

  const chooseMode = (nextMode) => {
    if (cascade && isCustom && nextMode === mode) {
      publishCascade({ mode, listIds, selectedIds: [...selectedIds] })
      setIsCustom(false)
    }
    setMode(nextMode)
  }

  const selectAllLists = () => setListIds(customerLists.map(l => l.id))
  const deselectAllLists = () => setListIds([])

  const modeLabel = cascade && isCustom ? t('cascade_labels.custom') : t(`${cascade ? 'cascade_labels' : 'mode_labels'}.${mode}`)

  let footerText
  if (mode === 'by_category') {
    footerText = t('categories_selected', { selected: listIds.length, total: customerLists.length })
  } else if (mode === 'all') {
    footerText = t('events_selected', { selected: events.length, total: events.length })
  } else {
    footerText = t('events_selected', { selected: selectedIds.size, total: events.length })
  }

  return (
    <div className="fep-container" data-category={categoryKey}>
      <div className="fep-footer" onClick={() => setOpen(v => !v)}>
        <div className="fep-footer-left">
          <FaIcon icon="calendar-o" className="fep-footer-icon" />
          <span className="fep-footer-text">{cascade && isCustom ? t('custom_summary') : footerText}</span>
        </div>
        <FaIcon icon={open ? 'chevron-up' : 'chevron-down'} className="fep-chevron" />
      </div>

      {open && (
        <div className="fep-body">
          <div className="fep-mode-selector">
            <span className="fep-mode-label">{modeLabel}</span>
            <div className="fep-mode-pill">
              <button
                className={`btn${!isCustom && mode === 'all' ? ' active' : ''}`}
                onClick={() => chooseMode('all')}
              >
                {t('modes.all')}
              </button>
              <button
                className={`btn${!isCustom && mode === 'by_category' ? ' active' : ''}`}
                onClick={() => chooseMode('by_category')}
              >
                {t('modes.by_category')}
              </button>
              <button
                className={`btn${!isCustom && mode === 'manual' ? ' active' : ''}`}
                onClick={() => chooseMode('manual')}
              >
                {t('modes.manual')}
              </button>
              {cascade && isCustom && (
                <button type="button" className="btn active" disabled>
                  {t('modes.custom')}
                </button>
              )}
            </div>
          </div>

          {mode === 'by_category' && !isCustom && (
            <div>
              <div className="fep-meta">
                <span className="fep-count">
                  {t('selected_count', { selected: listIds.length, total: customerLists.length })}
                </span>
                <button
                  className="fep-select-all-btn"
                  onClick={allListsSelected ? deselectAllLists : selectAllLists}
                >
                  {allListsSelected ? t('deselect_all') : t('select_all')}
                </button>
              </div>
              <div className="fep-list fep-category-list">
                {customerLists.length === 0 ? (
                  <p className="fep-empty">{t('no_categories')}</p>
                ) : customerLists.map(list => {
                  const isChecked = listIds.includes(list.id)
                  return (
                    <div
                      key={list.id}
                      className={`fep-item${isChecked ? ' fep-item--selected' : ''}`}
                      onClick={() => toggleList(list.id)}
                    >
                      <div className="fep-item-left">
                        <div className={`fep-check-box${isChecked ? ' fep-check-box--on' : ''}`} />
                        <span className="fep-item-name">{list.name}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {mode === 'manual' && !isCustom && (
            <div>
              <div className="fep-filters">
                <div className="fep-search-wrap">
                  <i className="fa fa-search fep-search-icon" />
                  <input
                    type="text"
                    className="fep-search-input"
                    placeholder={t('search_placeholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="fep-dropdowns">
                  <GlgSelect
                    items={seasonOptions}
                    value={seasonFilter}
                    onChange={val => setSeasonFilter(val)}
                    aria-label={t('season_filter_label')}
                  />
                  <GlgSelect
                    items={categoryOptions}
                    value={categoryFilter}
                    onChange={val => setCategoryFilter(val)}
                    aria-label={t('category_filter_label')}
                  />
                </div>
              </div>

              <div className="fep-meta">
                <span className="fep-count">
                  {t('selected_count', { selected: selectedIds.size, total: events.length })}
                </span>
                <button
                  className="fep-select-all-btn"
                  onClick={allFilteredSelected ? deselectAll : selectAll}
                >
                  {allFilteredSelected ? t('deselect_all') : t('select_all')}
                </button>
              </div>

              <div className="fep-list">
                {filteredEvents.length === 0 ? (
                  <p className="fep-empty">{t('no_events')}</p>
                ) : filteredEvents.map(event => {
                  const isSelected = selectedIds.has(String(event.id))
                  return (
                    <div
                      key={event.id}
                      className={`fep-item${isSelected ? ' fep-item--selected' : ''}`}
                      onClick={() => toggleEvent(event.id)}
                    >
                      <div className="fep-item-left">
                        <div className={`fep-check-box${isSelected ? ' fep-check-box--on' : ''}`} />
                        <span className="fep-item-name">{event.name}</span>
                      </div>
                      {event.category && (
                        <span className="fep-badge fep-badge--type">{event.category}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  )
}

FeedEventPicker.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    season_id: PropTypes.string,
    category: PropTypes.string,
    list_ids: PropTypes.arrayOf(PropTypes.string),
    selected: PropTypes.bool,
  })).isRequired,
  categoryKey: PropTypes.string.isRequired,
  initialMode: PropTypes.oneOf(['all', 'by_category', 'manual']),
  initialListIds: PropTypes.arrayOf(PropTypes.string),
  customerLists: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  seasons: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    current: PropTypes.bool,
  })),
  cascade: PropTypes.bool,
  initialCustom: PropTypes.bool,
}

FeedEventPicker.defaultProps = {
  initialMode: 'all',
  initialListIds: [],
  customerLists: [],
  seasons: [],
  cascade: false,
  initialCustom: false,
}

export default FeedEventPicker
